"use client";

import { useRef, forwardRef, ForwardedRef, useState, RefObject } from "react";
import { useRouter } from "next/navigation";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Theme } from "@mui/material/styles";

import { useStyles } from "./styles";
import { Nullable } from "@/types/common";
import { Generation as TGeneration, TransformationType } from "@/types/thread";
import { AlertDialogType } from "@/types/alert";
import { useAppDispatch } from "@/redux/store";
import { addAlertDialog, setRoomUUID, setGenerations } from "@/redux/actions";
import { getDisplayedTimeAgo } from "@/utils/date";
import Button from "@/components/common/PrimaryButton";
import PromptViewer from "./PromptViewer";
import UserDetails from "@/components/profile/UserDetails";
import GenerationCards from "./GenerationCards";
import SourceImages from "./SourceImages";
import Feedback from "./Feedback";
import ViewModeToggle from "../GenInputs/Controls/ViewModeToggle";
import ReportButton from "./ReportButton";
import Tags from "./Tags";
import ImageViewer from "@/components/common/ImageViewer";
import DeleteGeneration from "./DeleteGeneration";
import ViewComments from "./ViewComments";
import ShareButton from "@/components/social/share/ShareButton";
import UsePrompt from "./UsePrompt";
// import DynamicAd from "@/components/adverts/DynamicAd";

interface GenerationProps extends TGeneration {
  isSubscribed: boolean;
  editEnabled: boolean;
  showFeedControls: boolean;
  className?: string;
  ref?: ForwardedRef<HTMLDivElement>;
  generationRefs?: { [key: number]: RefObject<HTMLDivElement> };
}

const Generation = forwardRef<HTMLDivElement, GenerationProps>((props, ref) => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down("md")
  );
  const isPrivate = Boolean(props.editEnabled);
  const isFeed = Boolean(props.showFeedControls);
  const modelUsed = props.modelUsed;
  const { classes, cx } = useStyles({
    isPrivate,
    modelUsed,
  });

  const headerRef = useRef<Nullable<HTMLDivElement>>(null);
  const [selectedImageIndex, setSelectedImageIndex] =
    useState<Nullable<number>>(null);

  const handleViewThread = () => {
    dispatch(setRoomUUID(null));
    dispatch(setGenerations([]));
    router.push(`/idea/${props.roomUUID}`);
  };

  const handleUpgradeDialogOpen = () => {
    if (!props.isSubscribed) {
      dispatch(
        addAlertDialog({
          title: "Subscription Required",
          description: `You need a Premium or Professional subscription to ${
            modelUsed === TransformationType.Enhance ? "enhance" : "create HD"
          } generations!`,
          nextButtonText: "Upgrade",
          type: AlertDialogType.SubscriptionRequired,
        })
      );
    }
  };

  const handleOpenImageViewer = (imageIndex: number) => {
    if (props.slug) {
      const imageId = props.images[imageIndex].id;
      router.push(`/gen/${props.slug}-${props.id}/?highlighted=${imageId}`);
    } else {
      setSelectedImageIndex(imageIndex);
    }
  };

  const handleCloseImageViewer = () => {
    setSelectedImageIndex(null);
  };

  return (
    <div
      className={cx(classes.container, props.className)}
      ref={ref}
      id={props.id.toString()}
    >
      <div className={classes.promptContainer}>
        <PromptViewer rawPrompt={props.prompt} textPrompt={props.textPrompt} />
      </div>
      <div className={classes.header} ref={headerRef}>
        <UserDetails
          username={props.username}
          avatarUrl={props.avatarUrl}
          showAvatar={isFeed}
          isPrivate={isPrivate}
          subscriptionType={props.subscriptionType}
        />
        <div className={classes.badges}>
          {props.slug && (
            <ShareButton
              url={`${process.env.NEXT_PUBLIC_BASE_URL}/gen/${props.slug}-${props.id}`}
            />
          )}
          {isPrivate && (
            <div className={classes.viewModeToggleContainer}>
              <ViewModeToggle
                generationViewMode={props.viewMode}
                generationId={props.id}
                scale={1.5}
              />
            </div>
          )}
          <Chip
            label="Enhanced"
            className={classes.enhanceBadge}
            onClick={handleUpgradeDialogOpen}
          />
          <Chip
            label="Upscaled"
            className={classes.upscaleBadge}
            onClick={handleUpgradeDialogOpen}
          />
          <Chip
            label="HD"
            className={classes.hdBadge}
            onClick={handleUpgradeDialogOpen}
          />
          <Chip
            label="HDR+"
            className={classes.hdrBadge}
            onClick={handleUpgradeDialogOpen}
          />
        </div>
      </div>
      <Tags tags={props.combinedTags} />
      <SourceImages
        rawImageUrl={props.rawImageUrl}
        inspirationImages={props.inspirationImageUrls}
        isSubscribed={props.isSubscribed}
        modelUsed={props.modelUsed}
      />
      <GenerationCards
        images={props.images}
        editEnabled={props.editEnabled}
        onImageClick={handleOpenImageViewer}
        modelUsed={props.modelUsed}
      />
      <div className={classes.footerControls}>
        <div className={classes.dateCreated}>
          <Typography variant="body1">
            {getDisplayedTimeAgo(props.createdAt)}
          </Typography>
          {props.editEnabled && !isFeed && <DeleteGeneration id={props.id} />}
        </div>
        {isPrivate ? (
          <Feedback
            generationId={props.id}
            feedbackRating={props.feedbackRating}
            feedbackComment={props.feedbackComment}
            existingFeedbackTooSlow={props.feedbackTooSlow}
            existingFeedbackWrongOutput={props.feedbackWrongOutput}
          />
        ) : (
          <div className={classes.publicFooterControls}>
            {!isFeed && <UsePrompt prompt={props.textPrompt} />}
            <ReportButton
              url={`${process.env.NEXT_PUBLIC_BASE_URL}/idea/${props.roomUUID}`}
              generationUrls={props.images.map((image) => image.url)}
            />
          </div>
        )}
      </div>
      {isFeed && (
        <div className={classes.feedControls}>
          <ViewComments
            roomUUID={props.roomUUID}
            totalComments={props.commentCount}
          />
          <ShareButton
            url={`${process.env.NEXT_PUBLIC_BASE_URL}/idea/${props.roomUUID}`}
          />
          <UsePrompt prompt={props.textPrompt || props.prompt?.toString()} />
          <Button variant="outlined" size="small" onClick={handleViewThread}>
            {isMobile ? "View" : "View Thread"}
          </Button>
        </div>
      )}
      {selectedImageIndex !== null && (
        <ImageViewer
          initialSelection={selectedImageIndex}
          images={
            props.isSubscribed
              ? props.images
              : props.images.length === 4
              ? props.images.slice(0, 2)
              : props.images // TODO: Backend should only lock 2 images instead of 3
          }
          onClose={handleCloseImageViewer}
          roomUUID={props.roomUUID}
        />
      )}
      {/* <DynamicAd variant="in-content-repeatable" /> */}
    </div>
  );
});

export default Generation;

Generation.displayName = "Generation";
